import { useTranslation } from "react-i18next";
import {
  BaseAssignmentResponseDto,
  LearningOutcomeUniqueResponseDto,
  TeacherDocumentResponseDto,
  TopicResponseDto,
  MaarifLearningOutcomesPerOutcomePerExplanationLevel2ResponseDto,
} from "@packages/api/madlenAPI.schemas";
import { FileText } from "lucide-react";
import { memo } from "react";

type ConstraintType =
  | { type: "youtubeLink"; value: string }
  | { type: "learningOutcomes"; value: LearningOutcomeUniqueResponseDto[] }
  | { type: "topics"; value: TopicResponseDto[] }
  | { type: "teacherDocuments"; value: TeacherDocumentResponseDto[] }
  | { type: "driveFileIds"; value: string[] }
  | { type: "prompt"; value: string }
  | {
      type: "maarifLearningOutcomes";
      value: MaarifLearningOutcomesPerOutcomePerExplanationLevel2ResponseDto[];
    };

function getAssignmentConstraint(
  assignment: BaseAssignmentResponseDto,
): ConstraintType | null {
  if (assignment.youtubeLink) {
    return { type: "youtubeLink", value: assignment.youtubeLink };
  }

  if (assignment?.learningOutcomes?.length) {
    return { type: "learningOutcomes", value: assignment.learningOutcomes };
  }

  if (assignment?.topics?.length) {
    return { type: "topics", value: assignment.topics };
  }

  if (assignment?.teacherDocuments?.length) {
    return { type: "teacherDocuments", value: assignment.teacherDocuments };
  }

  if (assignment?.driveFileIds?.length) {
    return { type: "driveFileIds", value: assignment.driveFileIds };
  }

  if (assignment?.prompt) {
    return { type: "prompt", value: assignment.prompt };
  }

  if (assignment?.maarifLearningOutcomes?.length) {
    return {
      type: "maarifLearningOutcomes",
      value: assignment.maarifLearningOutcomes,
    };
  }

  return null;
}

interface AssignmentConstraintsProps {
  assignment: BaseAssignmentResponseDto;
}

const AssignmentConstraints = memo(
  ({ assignment }: AssignmentConstraintsProps) => {
    const { t } = useTranslation();
    const constraint = getAssignmentConstraint(assignment);

    if (!constraint) return null;

    switch (constraint.type) {
      case "youtubeLink": {
        const { value: youtubeLink } = constraint;
        const videoId = youtubeLink.includes("youtu.be")
          ? youtubeLink.split("youtu.be/")[1]?.split("?")[0]
          : youtubeLink.includes("/embed/")
            ? youtubeLink.split("/embed/")[1]?.split("?")[0]
            : youtubeLink.split("v=")[1]?.split("&")[0];

        return (
          <div className="aspect-video w-1/2 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden items-center justify-center">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title="YouTube video player"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        );
      }

      case "learningOutcomes": {
        const { value: outcomes } = constraint;
        return (
          <div className="flex flex-wrap gap-2">
            {outcomes.slice(0, 3).map((outcome, index) => (
              <div
                key={index}
                className="rounded-full px-4 py-2  bg-green-100 dark:bg-green-900 border-2 border-white dark:border-gray-800 flex items-center justify-center"
              >
                <div className="text-xs font-medium text-green-900 dark:text-green-100">
                  {outcome.outcome}
                </div>
              </div>
            ))}
          </div>
        );
      }

      case "topics": {
        const { value: topics } = constraint;
        return (
          <div className="flex flex-wrap gap-2">
            {topics.slice(0, 3).map((topic, index) => (
              <div
                key={index}
                className="rounded-full px-4 py-1 bg-indigo-100 dark:bg-indigo-900 border-2 border-white dark:border-gray-800 flex items-center justify-center"
              >
                <div className="text-xs font-medium text-indigo-900 dark:text-indigo-100">
                  {topic.topic}
                </div>
              </div>
            ))}
          </div>
        );
      }

      case "teacherDocuments": {
        const { value: docs } = constraint;
        return (
          <div className="flex flex-wrap gap-2">
            {docs.map((doc, index) => (
              <div
                key={index}
                className="rounded-full px-4 py-1 bg-blue-100 dark:bg-blue-900 border-2 border-white dark:border-gray-800 flex items-center justify-center"
              >
                <div className="text-xs font-medium text-blue-900 dark:text-blue-100 flex items-center">
                  <FileText size={12} className="inline mr-1.5" />
                  <span className="truncate">{doc.fileName}</span>
                </div>
              </div>
            ))}
          </div>
        );
      }

      case "driveFileIds": {
        return (
          <div className="flex flex-wrap gap-2">
            <div className="rounded-full px-4 py-1 bg-orange-100 dark:bg-orange-900 border-2 border-white dark:border-gray-800 flex items-center justify-center">
              <div className="text-xs font-medium text-orange-900 dark:text-orange-100 flex items-center">
                <FileText size={12} className="inline mr-1.5" />
                <span>{t("assignments.card.generatedWithDriveFiles")}</span>
              </div>
            </div>
          </div>
        );
      }

      case "prompt": {
        const { value: prompt } = constraint;
        return (
          <div className="rounded-full px-4 py-1 bg-purple-100 dark:bg-purple-900 border-2 border-white dark:border-gray-800 flex items-center justify-center w-fit">
            <div className="text-xs font-medium text-purple-900 dark:text-purple-100">
              {prompt}
            </div>
          </div>
        );
      }

      case "maarifLearningOutcomes": {
        const { value: outcomes } = constraint;
        return (
          <div className="flex flex-wrap gap-2">
            {outcomes.slice(0, 3).map((outcome, index) => (
              <div
                key={index}
                className="rounded-full px-4 py-1 bg-teal-100 dark:bg-teal-900 border-2 border-white dark:border-gray-800 flex items-center justify-center"
              >
                <div className="text-xs font-medium text-teal-900 dark:text-teal-100">
                  {outcome.outcome}
                </div>
              </div>
            ))}
          </div>
        );
      }
    }
  },
);

export default AssignmentConstraints;
